/**
 * Solar position — NOAA / USNO low-precision algorithm.
 *
 * Pure math. No Cesium, no shaders, no network. Used by the SUN + SHADOW
 * STUDY layer to answer the EP advance-work question the 3D globe exists for:
 * where is the sun at event time, and therefore where is the glare, the
 * concealment, the sniper-hide, and the photographer's angle.
 *
 * Accuracy: ~0.01 deg in declination and ~0.1 deg in azimuth/elevation for
 * dates within a few decades of J2000 — far tighter than any operational
 * shadow/glare decision needs. Source of the formulae: the standard
 * USNO "Approximate Solar Coordinates" / NOAA Solar Calculator equations,
 * which are published reference math, not derived doctrine.
 */

const RAD = Math.PI / 180;

export interface SunPosition {
  /** Compass bearing of the sun, degrees clockwise from true north (0-360). */
  azimuth: number;
  /** Degrees above the horizon. Negative = below horizon (sun is down). */
  elevation: number;
}

/** Normalize degrees into [0, 360). */
function norm360(d: number): number {
  const r = d % 360;
  return r < 0 ? r + 360 : r;
}

/**
 * Sun azimuth/elevation for an instant and a point on the earth.
 *
 * @param date  Instant to evaluate (absolute time — UTC internally).
 * @param latDeg Latitude, degrees north positive.
 * @param lonDeg Longitude, degrees east positive.
 */
export function sunPosition(date: Date, latDeg: number, lonDeg: number): SunPosition {
  // Days since the J2000.0 epoch (2451545.0 JD).
  const jd = date.getTime() / 86_400_000 + 2440587.5;
  const n = jd - 2451545.0;

  // Mean longitude and mean anomaly of the sun.
  const L = norm360(280.46 + 0.9856474 * n);
  const g = norm360(357.528 + 0.9856003 * n) * RAD;

  // Ecliptic longitude (equation of center applied to mean longitude).
  const lambda = (L + 1.915 * Math.sin(g) + 0.02 * Math.sin(2 * g)) * RAD;

  // Obliquity of the ecliptic.
  const eps = (23.439 - 0.0000004 * n) * RAD;

  // Right ascension and declination.
  const alpha = Math.atan2(Math.cos(eps) * Math.sin(lambda), Math.cos(lambda));
  const delta = Math.asin(Math.sin(eps) * Math.sin(lambda));

  // Greenwich mean sidereal time -> local hour angle.
  const gmstHours = norm360((18.697374558 + 24.06570982441908 * n) * 15) / 15;
  const lst = gmstHours * 15 * RAD + lonDeg * RAD;

  let H = lst - alpha;
  while (H > Math.PI) H -= 2 * Math.PI;
  while (H < -Math.PI) H += 2 * Math.PI;

  const phi = latDeg * RAD;
  const sinEl =
    Math.sin(phi) * Math.sin(delta) + Math.cos(phi) * Math.cos(delta) * Math.cos(H);
  const elevation = Math.asin(Math.max(-1, Math.min(1, sinEl))) / RAD;

  const azimuth = norm360(
    Math.atan2(
      -Math.sin(H),
      Math.tan(delta) * Math.cos(phi) - Math.sin(phi) * Math.cos(H),
    ) / RAD,
  );

  return { azimuth, elevation };
}

/** Compass point for an azimuth, for glanceable readout. */
export function compassPoint(azimuth: number): string {
  const pts = ["N", "NNE", "NE", "ENE", "E", "ESE", "SE", "SSE",
               "S", "SSW", "SW", "WSW", "W", "WNW", "NW", "NNW"];
  return pts[Math.round(norm360(azimuth) / 22.5) % 16]!;
}

/**
 * Shadow direction = the bearing a shadow is CAST toward, i.e. 180 deg
 * opposite the sun. This is the number an advance actually uses when
 * standing at the venue.
 */
export function shadowBearing(azimuth: number): number {
  return norm360(azimuth + 180);
}
